import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Flex, Text, Badge, Button, IconButton, Spinner, SimpleGrid, Select, Input, Textarea,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb,
  FormControl, FormLabel, useToast, Switch, useColorModeValue, useDisclosure, useBreakpointValue,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Menu, MenuButton, MenuList, MenuItem, MenuDivider,
} from '@chakra-ui/react';
import { MdEdit, MdKeyboardArrowDown, MdKeyboardArrowUp, MdVisibility, MdVisibilityOff, MdMoreVert } from 'react-icons/md';
import Card from 'components/card/Card';
import Pagination, { usePaged, useServerPaged, useDebounced } from 'components/vdh/Pagination';
import { api } from 'lib/api';
import { useApi } from 'lib/useApi';
import { useAuth } from 'lib/auth';
import { BarsChart, AreaChart, StreakChart, HourChart, AccuracyBars, DonutChart } from 'components/vdh/Charts';
import BattleCard from 'components/vdh/BattleCard';

const SUBJECTS = [['english', 'Tiếng Anh'], ['japanese', 'Tiếng Nhật'], ['math', 'Toán']];
const SUBJECT_FILTERS = [['all', 'Tất cả môn'], ...SUBJECTS];
const PRESETS = [['all', 'Tất cả'], ['today', 'Hôm nay'], ['7d', '7 ngày'], ['30d', '30 ngày'], ['custom', 'Tự chọn']];
const pad2 = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
/** Đổi preset -> { from, to } (YYYY-MM-DD) để truyền lên API báo cáo. 'all' = không giới hạn ngày. */
function presetRange(preset, from, to) {
  if (preset === 'custom') return { from: from || undefined, to: to || undefined };
  if (preset === 'all') return {};
  const today = new Date();
  const t = fmtDate(today);
  if (preset === 'today') return { from: t, to: t };
  const days = preset === '7d' ? 6 : 29;
  const f = new Date(today);
  f.setDate(f.getDate() - days);
  return { from: fmtDate(f), to: t };
}
const DIFFS = [['easy', 'Dễ'], ['medium', 'Vừa'], ['hard', 'Khó'], ['superhard', 'Siêu khó']];
const MODES = [['quiz', 'Trắc nghiệm'], ['balloon', 'Bắn bóng'], ['adventure', 'Phiêu lưu']];
const ANSWER = [['choice', 'Chọn đáp án'], ['type', 'Gõ đáp án']];
const AVATARS = ['🦊', '🐨', '🐰', '🦁', '🐝', '🐿️', '🐼', '🐧', '🦉', '🐢', '🦄', '🐬'];
const avatarFor = (s = '') => AVATARS[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATARS.length];

export default function Devices() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {id
        ? <Detail id={id} onBack={() => navigate('/admin/devices')} />
        : <List onOpen={(devId) => navigate(`/admin/devices/${devId}`)} />}
    </Box>
  );
}

function List({ onOpen }) {
  const { account } = useAuth();
  const isAdmin = account?.role === 'admin';
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  // Lọc + phân trang trong DB; thống kê từng con cũng chỉ tính cho các máy của trang này.
  const pager = useServerPaged((p) => api.get(`/devices?page=${p.page}&pageSize=${p.pageSize}${dq.trim() ? `&q=${encodeURIComponent(dq.trim())}` : ''}`),
    { initialSize: 12, resetKey: dq.trim() });
  const { loading, error } = pager;
  const avaBg = useColorModeValue('linear-gradient(150deg,#eae3ff,#d7c7ff)', 'linear-gradient(150deg,#2a2358,#3a2f74)');
  if (error) return <Text color="red.500">{error}</Text>;
  if (!pager.total && !loading) {
    return <Card><Text>{dq.trim() ? 'Không tìm thấy máy khớp.' : 'Chưa có thiết bị. Đăng nhập tài khoản này trên máy của con để tự thêm.'}</Text></Card>;
  }
  return (
    <>
      {isAdmin && (
        <Flex mb="16px">
          <Input maxW="320px" size="sm" borderRadius="10px" placeholder="🔍 Tìm con / máy / phụ huynh..."
            value={q} onChange={(e) => setQ(e.target.value)} />
        </Flex>
      )}
      {!pager.pageItems.length ? <Card><Text>{loading ? 'Đang tải…' : 'Không tìm thấy thiết bị khớp.'}</Text></Card> : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="18px">
          {pager.pageItems.map((d) => (
        <Card key={d.id} p="0" overflow="hidden" cursor="pointer" onClick={() => onOpen(d.id)}
          transition=".18s" _hover={{ transform: 'translateY(-4px)', boxShadow: '0 24px 60px rgba(85,60,160,.18)' }}>
          <Box h="5px" bgGradient="linear(90deg, #5AC8FA, #7C5CFC)" />
          <Box p="18px">
            <Flex align="center" gap="13px" mb="13px">
              <Flex w="52px" h="52px" borderRadius="15px" bg={avaBg} align="center" justify="center" fontSize="27px" flex="0 0 auto">
                {d.child?.avatar || avatarFor(d.child?.name)}
              </Flex>
              <Box flex="1">
                <Flex align="center" gap="7px"><Text fontWeight="800" fontSize="17px">{d.child?.name || '(chưa gán con)'}</Text>
                  <Box w="9px" h="9px" borderRadius="full" bg={d.online ? 'green.400' : 'gray.300'}
                    boxShadow={d.online ? '0 0 0 4px rgba(34,197,94,.16)' : 'none'} />
                  {!d.active && <Badge colorScheme="red" fontSize="10px">Tắt</Badge>}</Flex>
                <Text fontSize="12.5px" color="secondaryGray.600" mt="2px">{d.platform || '?'}{d.online ? ' · online' : ' · offline'}</Text>
              </Box>
            </Flex>
            <Flex gap="6px" mb="14px" wrap="wrap">
              {d.child && <Badge colorScheme="purple" variant="subtle">{String(d.child.grade).toUpperCase()}</Badge>}
              {d.child && <Badge variant="subtle">{d.child.subject}</Badge>}
              {isAdmin && d.account && <Badge variant="subtle">👤 {d.account.displayName || d.account.email}</Badge>}
            </Flex>
            {d.stats && (
              <SimpleGrid columns={3} gap="8px" mb="14px">
                <Mini n={`💎${d.stats.balance}`} l="Kim cương" />
                <Mini n={`${d.stats.accuracy}%`} l="Chính xác" />
                <Mini n={`${d.stats.today}′`} l="Hôm nay" />
              </SimpleGrid>
            )}
            <Button w="100%" size="sm" colorScheme="brand">Xem báo cáo →</Button>
          </Box>
        </Card>
          ))}
        </SimpleGrid>
      )}
      <Pagination pager={pager} label="thiết bị" />
    </>
  );
}
function Mini({ n, l }) {
  const bg = useColorModeValue('#FBFAFF', '#1E1840');
  return (
    <Box bg={bg} borderRadius="12px" py="9px" textAlign="center">
      <Text fontWeight="850" fontSize="15px">{n}</Text><Text fontSize="10.5px" color="secondaryGray.600">{l}</Text>
    </Box>
  );
}

function Detail({ id, onBack }) {
  const toast = useToast();
  const { account } = useAuth();
  const isAdmin = account?.role === 'admin';
  const { data, loading, error, reload } = useApi(() => api.get(`/devices/${id}`), [id]);
  const childId = data?.child?.id;

  // Bộ lọc báo cáo: khoảng ngày (hôm nay / 7 / 30 ngày / tự chọn) + môn học.
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('all');
  const range = presetRange(preset, from, to);
  const qs = new URLSearchParams();
  if (range.from) qs.set('from', range.from);
  if (range.to) qs.set('to', range.to);
  if (subject !== 'all') qs.set('subject', subject);
  const qsStr = qs.toString();

  const rep = useApi(
    () => (childId ? api.get(`/reports/children/${childId}/report${qsStr ? `?${qsStr}` : ''}`) : Promise.resolve(null)),
    [childId, qsStr],
  );
  const avaBg = useColorModeValue('linear-gradient(150deg,#eae3ff,#d7c7ff)', 'linear-gradient(150deg,#2a2358,#3a2f74)');
  const diamond = useDisclosure();   // modal sửa 💎
  const move = useDisclosure();      // modal chuyển phụ huynh (admin)
  const avatar = useDisclosure();    // modal đổi avatar
  const rename = useDisclosure();    // modal đổi tên máy

  if (loading) return <Spinner />;
  if (error) return <Text color="red.500">{error}</Text>;
  const d = data, s = d.stats;
  const acc = s && s.total ? Math.round((s.correct / s.total) * 100) : 0;
  const m = rep.data?.mastery;
  const mpct = m && m.total ? Math.round((m.done / m.total) * 100) : 0;

  async function toggleActive() {
    try { await api.patch(`/devices/${id}`, { active: !d.active }); toast({ title: d.active ? 'Đã tắt máy' : 'Đã bật máy', status: 'success' }); reload(); }
    catch (e) { toast({ title: e.message, status: 'error' }); }
  }
  async function removeDevice() {
    if (!window.confirm('Xoá hẳn thiết bị này?')) return;
    try { await api.del(`/devices/${id}`); toast({ title: 'Đã xoá thiết bị', status: 'success' }); onBack(); }
    catch (e) { toast({ title: e.message, status: 'error' }); }
  }

  return (
    <>
      <Flex align="center" mb="12px">
        <Button variant="ghost" onClick={onBack}>‹ Danh sách</Button>
      </Flex>

      <Card mb="16px">
        <Flex align="flex-start" gap="14px">
          <Flex w="60px" h="60px" borderRadius="17px" bg={avaBg} align="center" justify="center" fontSize="32px" flex="0 0 auto">{d.child?.avatar || avatarFor(d.child?.name)}</Flex>
          <Box flex="1" minW="0">
            <Flex align="center" gap="9px" wrap="wrap"><Text fontWeight="850" fontSize="21px">{d.child?.name || '(chưa gán con)'}</Text>
              <Box w="10px" h="10px" borderRadius="full" bg={d.online ? 'green.400' : 'gray.300'} />
              {!d.active && <Badge colorScheme="red">Đã tắt</Badge>}</Flex>
            {d.account && <Text fontSize="13.5px" color="secondaryGray.600" mt="2px">👤 {d.account.displayName || d.account.email}</Text>}
            <Flex align="center" gap="6px" mt="3px">
              <Text fontSize="14px" fontWeight="700">📱 {d.name || d.model || d.platform || 'Thiết bị'}</Text>
              <IconButton aria-label="Đổi tên máy" title="Đổi tên máy" size="xs" variant="ghost" icon={<MdEdit size={15} />} onClick={rename.onOpen} />
            </Flex>
            <Text fontSize="12.5px" color="secondaryGray.600" mt="1px">
              {(d.platform || '?')}{d.model ? ` · ${d.model}` : ''}{d.osVer ? ` · ${d.osVer}` : ''}{d.appVersion ? ` · v${d.appVersion}` : ''}</Text>
          </Box>
          <Menu placement="bottom-end">
            <MenuButton as={IconButton} icon={<MdMoreVert size={22} />} variant="ghost" aria-label="Thao tác" flex="0 0 auto" />
            <MenuList minW="200px">
              {d.child && <MenuItem onClick={diamond.onOpen}>💎 Sửa kim cương</MenuItem>}
              {isAdmin && d.child && <MenuItem onClick={move.onOpen}>↪️ Chuyển phụ huynh</MenuItem>}
              {d.child && <MenuItem onClick={avatar.onOpen}>🖼️ Đổi avatar</MenuItem>}
              <MenuItem onClick={toggleActive}>{d.active ? '⏸️ Tắt máy' : '▶️ Bật máy'}</MenuItem>
              <MenuDivider />
              <MenuItem color="red.500" onClick={removeDevice}>🗑️ Xoá thiết bị</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
        {d.lastError && <Text mt="10px" fontSize="sm" color="orange.400">⚠️ Lỗi gần nhất: {d.lastError}</Text>}
      </Card>

      {/* modals điều khiển từ menu ... */}
      {d.child && <DiamondModal isOpen={diamond.isOpen} onClose={diamond.onClose} childId={d.child.id} balance={s?.balance ?? 0} onDone={() => { reload(); rep.reload(); }} />}
      {isAdmin && d.child && <MoveModal isOpen={move.isOpen} onClose={move.onClose} childId={d.child.id} currentAccountId={d.account?.id} childName={d.child?.name} onDone={onBack} />}
      {d.child && <AvatarModal isOpen={avatar.isOpen} onClose={avatar.onClose} childId={d.child.id} current={d.child?.avatar} onDone={reload} />}
      <DeviceRenameModal isOpen={rename.isOpen} onClose={rename.onClose} deviceId={id} current={d.name} fallback={d.model || d.platform} onDone={reload} />

      {/* Khoá máy từ xa là tính năng kiosk chỉ có trên Android — ẩn với iOS. */}
      {d.child && !/ios/i.test(d.platform || '') && <LockCard child={d.child} onDone={reload} />}

      {d.child && <ConfigCard child={d.child} onSaved={() => { reload(); rep.reload(); }} />}

      {d.child && <ChildBattles childId={d.child.id} />}
      {d.child && <RewardToggles accountId={d.account?.id} childId={d.child.id} isAdmin={isAdmin} />}
      {d.child && <ChildRedemptions childId={d.child.id} />}

      {/* 🔎 Bộ lọc báo cáo — áp cho toàn bộ số liệu & biểu đồ bên dưới */}
      {d.child && (
        <Card mb="16px">
          <Flex align="center" gap="10px" mb="12px" wrap="wrap">
            <Text fontWeight="850" fontSize="16px">🔎 Lọc báo cáo</Text>
            {rep.loading && <Spinner size="sm" color="brand.500" />}
          </Flex>
          <Flex gap="8px" wrap="wrap" align="center">
            {PRESETS.map(([v, l]) => (
              <Button key={v} size="sm" colorScheme="brand" variant={preset === v ? 'solid' : 'outline'}
                onClick={() => setPreset(v)}>{l}</Button>
            ))}
            <Box flex="1" minW="8px" />
            <Select size="sm" w="160px" borderRadius="10px" value={subject} onChange={(e) => setSubject(e.target.value)}>
              {SUBJECT_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </Flex>
          {preset === 'custom' && (
            <Flex gap="14px" wrap="wrap" align="center" mt="12px">
              <Flex align="center" gap="7px"><Text fontSize="sm" color="secondaryGray.600">Từ ngày</Text>
                <Input size="sm" type="date" w="170px" borderRadius="10px" max={to || undefined} value={from} onChange={(e) => setFrom(e.target.value)} /></Flex>
              <Flex align="center" gap="7px"><Text fontSize="sm" color="secondaryGray.600">Đến ngày</Text>
                <Input size="sm" type="date" w="170px" borderRadius="10px" min={from || undefined} value={to} onChange={(e) => setTo(e.target.value)} /></Flex>
            </Flex>
          )}
        </Card>
      )}

      {s && (() => {
        const t = rep.data?.totals;
        const num = (v) => (typeof v === 'number' ? v.toLocaleString('vi-VN') : '—');
        return (
          <SimpleGrid columns={{ base: 2, md: 4 }} gap="14px" mb="16px">
            <Stat4 ic="💎" bg="rgba(255,207,74,.18)" n={num(s.balance)} l="Kim cương" />
            <Stat4 ic="✅" bg="rgba(34,197,94,.15)" n={num(t?.correct)} l="Câu đúng" />
            <Stat4 ic="❌" bg="rgba(248,113,113,.16)" n={num(t?.wrong)} l="Câu sai" />
            <Stat4 ic="🎯" bg="rgba(124,92,252,.14)" n={`${t?.accuracy ?? acc}%`} l="Độ chính xác" />
            <Stat4 ic="🔥" bg="rgba(255,159,64,.16)" n={rep.data?.bestStreak ?? '—'} l="Chuỗi tốt nhất" />
            <Stat4 ic="🔓" bg="rgba(56,189,248,.16)" n={num(t?.sessions)} l="Lần mở máy" />
            <Stat4 ic="⏱️" bg="rgba(56,189,248,.16)" n={`${t?.minutes ?? s.minutes}′`} l="Tổng số phút" />
            {m && <Stat4 ic="🏆" bg="rgba(34,197,94,.15)" n={num(m.done)} l="Câu đã thạo" />}
          </SimpleGrid>
        );
      })()}

      {/* ⭐/🌟 Tồn kho sao Kho Báu */}
      {d.child && (
        <SimpleGrid columns={2} gap="14px" mb="16px">
          <Stat4 ic="⭐" bg="rgba(124,92,252,.14)" n={d.child.rescueStars ?? 0} l="Sao Hộ Mệnh" />
          <Stat4 ic="🌟" bg="rgba(34,197,94,.15)" n={d.child.luckyStars ?? 0} l="Sao May Mắn" />
        </SimpleGrid>
      )}

      {/* Tiến độ chương trình */}
      {m && (
        <Card mb="16px" p="0" overflow="hidden">
          <Flex align="center" gap="12px" p="16px 18px" wrap="wrap">
            <Text fontSize="22px">🏅</Text>
            <Box><Text fontWeight="850" fontSize="15px">Đã làm đúng <Text as="span" color="brand.500">{m.done.toLocaleString('vi-VN')} / {m.total.toLocaleString('vi-VN')} câu</Text> của chương trình</Text>
              <Text fontSize="12.5px" color="secondaryGray.600">Còn {Math.max(0, m.total - m.done).toLocaleString('vi-VN')} câu chưa thạo</Text></Box>
            <Text ml="auto" fontWeight="850" fontSize="22px" color="brand.500">{mpct}%</Text>
          </Flex>
          <Box h="10px" bg="blackAlpha.200"><Box h="100%" w={`${mpct}%`} bgGradient="linear(90deg, #22C55E, #4ade80)" /></Box>
        </Card>
      )}

      {/* Charts */}
      {rep.loading ? <Spinner /> : rep.data && (
        <SimpleGrid columns={1} gap="18px" mb="16px">
          <Card><Text fontWeight="800" fontSize="14.5px" mb="2px">📊 Đúng / Sai theo ngày</Text>
            <Flex gap="16px" fontSize="12px" color="secondaryGray.600" mb="4px">
              <span><Box as="span" display="inline-block" w="10px" h="10px" borderRadius="3px" bg="#22C55E" mr="5px" />Đúng</span>
              <span><Box as="span" display="inline-block" w="10px" h="10px" borderRadius="3px" bg="#F87171" mr="5px" />Sai</span></Flex>
            <BarsChart data={rep.data.perDay} /></Card>
          <Card><Text fontWeight="800" fontSize="14.5px" mb="6px">📚 Số phút học mỗi ngày <Text as="span" fontWeight="500" color="secondaryGray.600" fontSize="12px">· thời gian ngồi làm bài (từ bản app mới)</Text></Text>
            <AreaChart data={rep.data.perDay.map((d) => ({ day: d.day, minutes: d.studyMinutes ?? 0 }))} tipLabel="phút học" /></Card>
          <Card><Text fontWeight="800" fontSize="14.5px" mb="6px">⏱️ Số phút mở máy mỗi ngày <Text as="span" fontWeight="500" color="secondaryGray.600" fontSize="12px">· thời gian được chơi</Text></Text>
            <AreaChart data={rep.data.perDay} tipLabel="phút mở máy" /></Card>
          <Card gridColumn={{ md: '1 / -1' }}>
            <Text fontWeight="800" fontSize="14.5px" mb="6px">🎯 Số câu đúng mỗi chuỗi <Text as="span" fontWeight="500" color="secondaryGray.600" fontSize="12px">· xanh = đủ chuỗi mở máy · đỏ = làm lại</Text></Text>
            <StreakChart data={rep.data.streak} /></Card>
        </SimpleGrid>
      )}

      {/* Báo cáo phân tích thêm: theo độ khó · theo chủ đề · theo giờ · câu sai nhiều nhất */}
      {rep.data && (
        <>
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap="18px" mb="16px">
            {rep.data.byLevel?.length > 0 && (
              <Card>
                <Text fontWeight="800" fontSize="14.5px" mb="12px">🎚️ Độ chính xác theo độ khó</Text>
                <AccuracyBars rows={rep.data.byLevel.map((r) => ({ label: `Mức ${r.level}`, pct: r.pct, total: r.total, correct: r.correct }))} />
              </Card>
            )}
            {rep.data.byTopic?.length > 0 && (
              <Card>
                <Text fontWeight="800" fontSize="14.5px" mb="12px">📚 Độ chính xác theo chủ đề</Text>
                <AccuracyBars rows={rep.data.byTopic.map((r) => ({ label: r.topic, pct: r.pct, total: r.total, correct: r.correct }))} />
              </Card>
            )}
          </SimpleGrid>

          {/* 🕐 Giờ học + 🥧 Tỉ lệ câu đúng — chung 1 row */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap="18px" mb="16px">
            {rep.data.byHour?.length > 0 && (
              <Card>
                <Text fontWeight="800" fontSize="14.5px" mb="2px">🕐 Giờ học trong ngày <Text as="span" fontWeight="500" color="secondaryGray.600" fontSize="12px">· số câu trả lời theo khung giờ</Text></Text>
                <HourChart data={rep.data.byHour} />
              </Card>
            )}
            {rep.data.totals && (rep.data.totals.correct + rep.data.totals.wrong) > 0 && (
              <Card>
                <Text fontWeight="800" fontSize="14.5px" mb="12px">🥧 Tỉ lệ câu đúng trên tổng câu trả lời</Text>
                <DonutChart correct={rep.data.totals.correct} wrong={rep.data.totals.wrong} />
              </Card>
            )}
          </SimpleGrid>

          <TopWrong childId={childId} qsStr={qsStr} />
        </>
      )}

      {/* Nhật ký lỗi crash */}
      {d.child && <CrashLog childId={d.child.id} qsStr={qsStr} />}

      {/* Nhật ký câu trả lời */}
      {childId && <AnswersLog childId={childId} qsStr={qsStr} />}
    </>
  );
}

/** 🔒 Khoá / mở máy con TỪ XA + trạng thái phút còn lại (gói Pro). Lệnh áp ở lần con đồng bộ kế (~10–15s). */
function LockCard({ child, onDone }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [mins, setMins] = useState(30);
  const locked = child.locked !== false;
  const remain = Math.round((child.remainingSec || 0) / 60);
  async function run(fn, label) {
    setBusy(true);
    try { await fn(); toast({ title: label, status: 'success' }); onDone && onDone(); }
    catch (e) { toast({ title: e.message, status: 'error' }); } finally { setBusy(false); }
  }
  return (
    <Card mb="16px">
      <Flex align="center" gap="10px" mb="4px">
        <Text fontWeight="850" fontSize="16px">🔒 Khoá máy từ xa</Text>
        <Badge colorScheme={locked ? 'red' : 'green'}>{locked ? 'Đang khoá' : `Đang mở · còn ${remain}′`}</Badge>
      </Flex>
      <Text fontSize="sm" color="secondaryGray.600" mb="12px">Lệnh áp trên máy con ở lần đồng bộ kế (~10–15 giây). Cần gói Pro + máy con đang online.</Text>
      <Flex gap="8px" wrap="wrap" align="center">
        <Button colorScheme="red" isLoading={busy}
          onClick={() => run(() => api.post(`/children/${child.id}/lock`), 'Đã gửi lệnh khoá')}>🔒 Khoá ngay</Button>
        <Button colorScheme="green" isLoading={busy}
          onClick={() => run(() => api.post(`/children/${child.id}/unlock`), 'Đã mở 1 phiên')}>🔓 Mở 1 phiên</Button>
        <Flex gap="6px" align="center">
          <Input type="number" w="82px" value={mins} onChange={(e) => setMins(e.target.value)} />
          <Text fontSize="sm" color="secondaryGray.600">phút</Text>
          <Button variant="outline" colorScheme="green" isLoading={busy}
            onClick={() => run(() => api.post(`/children/${child.id}/unlock`, { minutes: Number(mins) }), `Đã mở ${mins} phút`)}>Mở</Button>
        </Flex>
        {[15, 30, 60, 120].map((m) => <Button key={m} size="sm" variant="ghost" onClick={() => setMins(m)}>{m}′</Button>)}
      </Flex>
    </Card>
  );
}

function RewardToggles({ accountId, childId, isAdmin }) {
  const toast = useToast();
  const path = isAdmin && accountId ? `/rewards/items?accountId=${accountId}` : '/rewards/items';
  const { data, loading, reload } = useApi(() => api.get(path), [accountId, childId]);
  async function toggle(itemId) {
    try { await api.post(`/rewards/items/${itemId}/toggle-child/${childId}`); reload(); }
    catch (e) { toast({ title: e.message, status: 'error' }); }
  }
  if (loading || !data) return null;
  if (!data.length) return null;
  return (
    <Card mb="16px">
      <Text fontWeight="800" fontSize="15px" mb="4px">🎁 Phần thưởng áp dụng cho bé này</Text>
      <Text fontSize="sm" color="secondaryGray.600" mb="12px">Bật/tắt từng phần thưởng cho riêng bé (chỉ ảnh hưởng bé này).</Text>
      {data.map((it) => {
        const on = !(it.offChildIds || []).includes(childId);
        return (
          <Flex key={it.id} align="center" py="9px" borderBottom="1px solid" borderColor="line">
            <Text fontSize="22px" me="10px">{it.icon}</Text>
            <Box flex="1"><Text fontWeight="700">{it.name}</Text><Text fontSize="12.5px" color="secondaryGray.600">💎 {it.cost}</Text></Box>
            <Switch isChecked={on} onChange={() => toggle(it.id)} colorScheme="green" />
          </Flex>
        );
      })}
    </Card>
  );
}

// Huy hiệu rank kiểu LMHT: Đồng → Bạc → Vàng → Kim Cương → Cao Thủ → Chiến Thần.
// img = huy hiệu gen bằng mflux (public/ranks); c1/c2 = màu fallback + glow.
const RANKS = [
  { name: 'Đồng', emoji: '🥉', img: '/ranks/rank_1_dong.png', c1: '#E0A46A', c2: '#8C5A2B' },
  { name: 'Bạc', emoji: '🥈', img: '/ranks/rank_2_bac.png', c1: '#CBD5E1', c2: '#7C8896' },
  { name: 'Vàng', emoji: '🥇', img: '/ranks/rank_3_vang.png', c1: '#F5C542', c2: '#C98A12' },
  { name: 'Kim Cương', emoji: '💎', img: '/ranks/rank_4_kimcuong.png', c1: '#67E8F9', c2: '#0E93B5' },
  { name: 'Cao Thủ', emoji: '👑', img: '/ranks/rank_5_caothu.png', c1: '#C084FC', c2: '#7A2FD0' },
  { name: 'Chiến Thần', emoji: '🔥', img: '/ranks/rank_6_chienthan.png', c1: '#FF7A7A', c2: '#C21E1E' },
];
const STAR_DIV = 5; const DIV_RANK = 5; const PER_RANK = STAR_DIV * DIV_RANK; // 25 thắng ròng / rank
// +1★ khi thắng, −1★ khi thua; 5★ lên 1 cấp; 5 cấp (25★) lên 1 rank.
function rankOf(wins, losses) {
  const net = Math.max(0, wins - losses);
  const idx = Math.min(RANKS.length - 1, Math.floor(net / PER_RANK));
  const within = net - idx * PER_RANK;
  const top = idx === RANKS.length - 1;
  return {
    idx,
    ...RANKS[idx],
    division: top ? DIV_RANK : Math.floor(within / STAR_DIV) + 1,
    stars: top ? STAR_DIV : within % STAR_DIV,
    starMax: STAR_DIV,
  };
}

/** 1 huy hiệu: ưu tiên ảnh gen; nếu chưa có ảnh -> fallback ô gradient + emoji. */
function RankIcon({ rank, size }) {
  const [ok, setOk] = useState(true);
  if (ok) {
    // chiều cao cố định, bề ngang tự co -> 6 huy hiệu cao bằng nhau
    return <img src={rank.img} alt={rank.name}
      style={{ height: `${size}px`, width: 'auto', display: 'block' }} onError={() => setOk(false)} />;
  }
  return (
    <Flex w={`${size}px`} h={`${size}px`} borderRadius="16px" align="center" justify="center" fontSize={`${Math.round(size * 0.5)}px`}
      color="#fff" bgGradient={`linear(150deg, ${rank.c1}, ${rank.c2})`} border="2px solid rgba(255,255,255,.35)">{rank.emoji}</Flex>
  );
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V'];
/** Full ladder 6 huy hiệu — cái đang đạt to & rõ; nhãn "Đồng II"; sao đặt trong dải ruy băng của huy hiệu. */
function RankLadder({ rank }) {
  const roman = ROMAN[rank.division - 1] || String(rank.division);
  const activeSize = useBreakpointValue({ base: 92, md: 132 }) || 132;
  const otherSize = useBreakpointValue({ base: 50, md: 72 }) || 72;
  return (
    <Flex gap={{ base: '4px', md: '10px' }} align="flex-start"
      justify={{ base: 'flex-start', xl: 'center' }} overflowX="auto" pb="6px" px="2px"
      sx={{ '::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
      {RANKS.map((r, i) => {
        const on = i === rank.idx;
        const size = on ? activeSize : otherSize;
        return (
          <Flex key={r.name} direction="column" align="center" gap="6px" flexShrink="0" pt={on ? '0' : '10px'}
            opacity={on ? 1 : 0.4} transition="all .2s"
            filter={on ? `drop-shadow(0 6px 16px ${r.c2}88)` : 'grayscale(0.55)'}>
            {/* huy hiệu (chỉ là emblem) — chiều cao cố định, cao đều */}
            <Box h={`${size}px`}><RankIcon rank={r} size={size} /></Box>
            {/* tên rank + cấp La Mã — text cố định, đồng bộ mọi rank */}
            <Text fontSize={on ? '14px' : '11px'} fontWeight={on ? '900' : '700'} whiteSpace="nowrap"
              color={on ? undefined : 'secondaryGray.600'} lineHeight="1.1">{on ? `${r.name} ${roman}` : r.name}</Text>
            {/* dải sao vàng render bằng CSS — vị trí CỐ ĐỊNH, không phụ thuộc ruy băng trong ảnh */}
            {on && (
              <Flex align="center" px="10px" py="2px" borderRadius="99px" minH="22px"
                bg="blackAlpha.100" _dark={{ bg: 'whiteAlpha.100' }}
                border="1px solid" borderColor="#F5C54266" boxShadow="0 2px 8px rgba(0,0,0,.12)">
                <Text color="#F5C542" fontSize="14px" fontWeight="900" letterSpacing="2px"
                  textShadow="0 1px 1px rgba(0,0,0,.4)">{'★'.repeat(rank.stars) || '☆'}</Text>
              </Flex>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}

function BStat({ n, l, c }) {
  return <Box textAlign="center"><Text fontWeight="900" fontSize="22px" color={c}>{n}</Text><Text fontSize="11px" color="secondaryGray.600">{l}</Text></Box>;
}

/** Huy hiệu rank (full ladder) + tổng thắng/thua + lịch sử thách đấu (thẻ) của RIÊNG bé này. */
function ChildBattles({ childId }) {
  const { data, loading, error } = useApi(() => api.get('/battles'), [childId]);
  const [showHistory, setShowHistory] = useState(false);
  if (loading || error) return null;
  const mine = (data || []).filter((b) => b.host?.id === childId || b.guest?.id === childId);
  const done = mine.filter((b) => b.status === 'done');
  const wins = done.filter((b) => b.winnerChildId === childId).length;
  const losses = done.length - wins;
  const rank = rankOf(wins, losses);
  const recent = [...done].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 8);
  return (
    <Card mb="16px">
      <Flex align="center" wrap="wrap" gap="12px" mb="14px">
        <Text fontWeight="800" fontSize="15px">⚔️ Thách đấu</Text>
        <Flex ml={{ base: '0', md: 'auto' }} gap="20px">
          <BStat n={wins} l="Thắng" c="green.400" />
          <BStat n={losses} l="Thua" c="red.400" />
          <BStat n={done.length} l="Tổng trận" />
        </Flex>
      </Flex>
      <RankLadder rank={rank} />
      <Flex align="center" gap="8px" mt="18px" mb="10px" cursor="pointer" onClick={() => setShowHistory((v) => !v)}
        role="button" _hover={{ color: 'brand.500' }}>
        <Text fontWeight="800" fontSize="13px" color="secondaryGray.600">LỊCH SỬ ĐẤU ({done.length})</Text>
        <Button ml="auto" size="xs" variant="ghost" colorScheme="brand" h="24px" minW="24px" px="6px"
          rightIcon={showHistory ? <MdKeyboardArrowUp size={18} /> : <MdKeyboardArrowDown size={18} />}>
          {showHistory ? 'Ẩn' : 'Hiện'}
        </Button>
      </Flex>
      {showHistory && (recent.length === 0 ? (
        <Text fontSize="sm" color="secondaryGray.600">Bé chưa đấu trận nào.</Text>
      ) : (
        <SimpleGrid columns={{ base: 1, lg: 2, xl: 3, '2xl': 4 }} gap="12px">
          {recent.map((b) => <BattleCard key={b.id} b={b} />)}
        </SimpleGrid>
      ))}
    </Card>
  );
}

const RST = { request: ['Chờ duyệt', 'orange'], approve: ['Đã duyệt', 'green'], reject: ['Từ chối', 'red'] };
/** Yêu cầu đổi quà đang chờ + lịch sử (đã duyệt/từ chối) của RIÊNG bé này. */
function ChildRedemptions({ childId }) {
  const toast = useToast();
  const [show, setShow] = useState(false);   // mặc định ẨN — bấm icon con mắt để hiện
  // Lọc theo con NGAY Ở SERVER (trước đây kéo toàn bộ yêu cầu của mọi con về rồi mới lọc).
  const { data, loading, error, reload } = useApi(
    () => api.get(`/rewards/redemptions?childId=${childId}&pageSize=100`),
    [childId],
  );
  const border = useColorModeValue('#EAE7F5', '#2A2350');
  const rows = (data?.items || []).slice().sort((a, b) => new Date(b.ts) - new Date(a.ts));
  const pending = rows.filter((r) => r.status === 'request').length;
  const pager = usePaged(rows, 10, childId);
  async function decide(id, action, reason) {
    try {
      await api.post(`/rewards/redemptions/${id}/${action}`, action === 'reject' ? { reason } : undefined);
      toast({ title: action === 'approve' ? 'Đã duyệt' : 'Đã từ chối', status: 'success' }); reload();
    } catch (e) { toast({ title: e.message, status: 'error' }); }
  }
  return (
    <Card mb="16px">
      <Flex align="center" gap="8px" mb="10px">
        <Text fontWeight="800" fontSize="15px">🎁 Yêu cầu &amp; lịch sử đổi quà</Text>
        {pending > 0 && <Badge colorScheme="purple">{pending} chờ duyệt</Badge>}
        {rows.length > 0 && <Text fontSize="12px" color="secondaryGray.600">({rows.length})</Text>}
        <IconButton ml="auto" size="sm" variant="ghost" colorScheme="brand"
          aria-label={show ? 'Ẩn danh sách' : 'Hiện danh sách'} title={show ? 'Ẩn' : 'Hiện'}
          icon={show ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
          onClick={() => setShow((v) => !v)} />
      </Flex>
      {show && (loading ? <Spinner /> : error ? <Text color="red.500">{error}</Text>
        : !rows.length ? <Text fontSize="sm" color="secondaryGray.600">Bé chưa có yêu cầu đổi quà nào.</Text>
        : (
          <>
            {pager.pageItems.map((r) => {
              const [label, scheme] = RST[r.status] || RST.request;
              return (
                <Flex key={r.id} align="center" gap="10px" py="10px" borderBottom="1px solid" borderColor={border}>
                  <Text fontSize="22px">{r.icon}</Text>
                  <Box flex="1">
                    <Text fontWeight="700" fontSize="14px">{r.itemName}</Text>
                    <Text fontSize="12px" color="secondaryGray.600">
                      💎 {r.cost} · {new Date(r.ts).toLocaleDateString('vi-VN')}
                      {r.status === 'reject' && r.reason ? ` · ${r.reason}` : ''}
                    </Text>
                  </Box>
                  {r.status === 'request' ? (
                    <Flex gap="6px">
                      <Button size="xs" colorScheme="green" onClick={() => decide(r.id, 'approve')}>Duyệt</Button>
                      <Button size="xs" variant="outline" colorScheme="red"
                        onClick={() => decide(r.id, 'reject', window.prompt('Lý do từ chối (tuỳ chọn):') || undefined)}>Từ chối</Button>
                    </Flex>
                  ) : <Badge colorScheme={scheme}>{label}</Badge>}
                </Flex>
              );
            })}
            <Pagination pager={pager} label="mục" />
          </>
        ))}
    </Card>
  );
}

/** Sửa kim cương của bé — phụ huynh (hồ sơ của mình) hoặc admin. Đặt số dư TUYỆT ĐỐI. */
function DiamondModal({ isOpen, onClose, childId, balance, onDone }) {
  const toast = useToast();
  const [val, setVal] = useState(String(balance ?? 0));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (isOpen) setVal(String(balance ?? 0)); }, [isOpen, balance]);
  async function save() {
    const set = parseInt(val, 10);
    if (Number.isNaN(set) || set < 0) { toast({ title: 'Nhập số 💎 hợp lệ (≥ 0)', status: 'warning' }); return; }
    setBusy(true);
    try {
      const r = await api.post(`/reports/children/${childId}/diamonds`, { set, reason: 'adjust' });
      toast({ title: `Đã cập nhật 💎 = ${r.balance}`, status: 'success' });
      onClose(); onDone && onDone();
    } catch (e) { toast({ title: e.message, status: 'error' }); } finally { setBusy(false); }
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Cập nhật kim cương</ModalHeader>
        <ModalBody>
          <Text fontSize="sm" color="secondaryGray.600" mb="8px">Đặt số 💎 mới cho bé (số dư hiện tại: {balance ?? 0}).</Text>
          <Input type="number" min={0} value={val} autoFocus onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Huỷ</Button>
          <Button colorScheme="brand" onClick={save} isLoading={busy}>Lưu</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** Chuyển hồ sơ con sang phụ huynh khác (gồm "Phụ huynh (chưa gán)") — CHỈ admin. */
function MoveModal({ isOpen, onClose, childId, currentAccountId, childName, onDone }) {
  const toast = useToast();
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  // API trả { items, total, ... }; lấy trang lớn vì đây là danh sách chọn trong modal.
  const { data: accounts } = useApi(() => (isOpen ? api.get('/admin/accounts?page=1&pageSize=100') : Promise.resolve(null)), [isOpen]);
  useEffect(() => { if (isOpen) setTarget(''); }, [isOpen]);
  const parents = (accounts?.items || [])
    .filter((a) => a.role === 'parent' && a.id !== currentAccountId)
    .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''));
  async function move() {
    if (!target) { toast({ title: 'Chọn phụ huynh đích', status: 'warning' }); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/children/${childId}/account`, { toAccountId: target });
      toast({ title: 'Đã chuyển hồ sơ sang phụ huynh mới', status: 'success' });
      onClose(); onDone && onDone();
    } catch (e) { toast({ title: e.message, status: 'error' }); } finally { setBusy(false); }
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Chuyển hồ sơ {childName ? `"${childName}"` : ''}</ModalHeader>
        <ModalBody>
          <Text fontSize="sm" color="secondaryGray.600" mb="8px">Chọn tài khoản phụ huynh nhận hồ sơ con này (kèm máy đã gắn).</Text>
          <Select placeholder="— Chọn phụ huynh —" value={target} onChange={(e) => setTarget(e.target.value)}>
            {parents.map((a) => (
              <option key={a.id} value={a.id}>
                {(a.displayName || a.email || a.id)} · {a.tier} · {a._count?.children ?? 0} con
              </option>
            ))}
          </Select>
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Huỷ</Button>
          <Button colorScheme="purple" onClick={move} isLoading={busy}>Chuyển</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** Đổi avatar (emoji) cho bé — phụ huynh hoặc admin. */
function AvatarModal({ isOpen, onClose, childId, current, onDone }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const border = useColorModeValue('#EAE7F5', '#2A2350');
  async function pick(av) {
    setBusy(true);
    try {
      await api.patch(`/children/${childId}`, { avatar: av });
      toast({ title: 'Đã đổi avatar', status: 'success' });
      onClose(); onDone && onDone();
    } catch (e) { toast({ title: e.message, status: 'error' }); } finally { setBusy(false); }
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Đổi avatar</ModalHeader>
        <ModalBody>
          <SimpleGrid columns={6} gap="8px">
            {AVATARS.map((av) => (
              <Flex key={av} as="button" disabled={busy} onClick={() => pick(av)} align="center" justify="center"
                h="46px" fontSize="26px" borderRadius="12px" borderWidth="2px"
                borderColor={av === current ? 'brand.500' : border} bg={av === current ? 'rgba(124,92,252,.12)' : 'transparent'}
                _hover={{ bg: 'rgba(124,92,252,.12)' }}>{av}</Flex>
            ))}
          </SimpleGrid>
        </ModalBody>
        <ModalFooter><Button variant="ghost" onClick={onClose}>Đóng</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** Đổi tên máy (phụ huynh/admin). Trống -> hiện model. */
function DeviceRenameModal({ isOpen, onClose, deviceId, current, fallback, onDone }) {
  const toast = useToast();
  const [val, setVal] = useState(current || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (isOpen) setVal(current || ''); }, [isOpen, current]);
  async function save() {
    setBusy(true);
    try {
      await api.patch(`/devices/${deviceId}`, { name: val.trim() });
      toast({ title: 'Đã đổi tên máy', status: 'success' });
      onClose(); onDone && onDone();
    } catch (e) { toast({ title: e.message, status: 'error' }); } finally { setBusy(false); }
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Đổi tên máy</ModalHeader>
        <ModalBody>
          <Text fontSize="sm" color="secondaryGray.600" mb="8px">Đặt tên gợi nhớ cho máy (để trống sẽ hiện “{fallback || '—'}”).</Text>
          <Input value={val} autoFocus maxLength={40} placeholder={fallback || 'Tên máy'}
            onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Huỷ</Button>
          <Button colorScheme="brand" onClick={save} isLoading={busy}>Lưu</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function CrashLog({ childId, qsStr }) {
  const toast = useToast();
  const [hideFixed, setHideFixed] = useState(false);
  // Nhật ký lỗi tải riêng (tối đa 40 bản ghi) thay vì đi kèm báo cáo tổng quan.
  const { data, reload } = useApi(
    () => api.get(`/reports/children/${childId}/errors${qsStr ? `?${qsStr}` : ''}`),
    [childId, qsStr],
  );
  const errors = data?.items || [];
  const list = errors.filter((e) => !hideFixed || !e.fixed);
  async function toggleFix(eventId) {
    try { await api.post(`/reports/children/${childId}/errors/fix`, { eventId }); reload(); }
    catch (e) { toast({ title: e.message, status: 'error' }); }
  }
  if (!errors.length) return null;
  return (
    <Card mt="16px">
      <Flex align="center" mb="10px"><Text fontWeight="800" fontSize="15.5px">🐞 Nhật ký lỗi ({errors.length})</Text>
        <Button ml="auto" size="xs" variant={hideFixed ? 'solid' : 'ghost'} colorScheme="brand" onClick={() => setHideFixed((v) => !v)}>Ẩn đã fix</Button></Flex>
      {list.map((e) => (
        <Flex key={e.id} align="flex-start" gap="10px" py="10px" borderBottom="1px solid" borderColor="line">
          <Box flex="1">
            <Text fontSize="13px" color={e.fixed ? 'secondaryGray.600' : 'orange.400'} textDecoration={e.fixed ? 'line-through' : 'none'}>{e.message}</Text>
            <Text fontSize="11.5px" color="secondaryGray.600" mt="2px">{new Date(e.ts).toLocaleString('vi-VN')}</Text>
          </Box>
          <Button size="xs" variant={e.fixed ? 'solid' : 'outline'} colorScheme="green" onClick={() => toggleFix(e.id)}>{e.fixed ? '✓ Đã fix' : 'Đánh dấu fix'}</Button>
        </Flex>
      ))}
      {!list.length && <Text fontSize="sm" color="secondaryGray.600">Không có lỗi nào (chưa fix).</Text>}
    </Card>
  );
}

function Stat4({ ic, bg, n, l }) {
  return (
    <Card py="15px"><Flex align="center" gap="13px">
      <Flex w="44px" h="44px" borderRadius="13px" bg={bg} align="center" justify="center" fontSize="21px" flex="0 0 auto">{ic}</Flex>
      <Box><Text fontSize="22px" fontWeight="850" lineHeight="1">{n}</Text><Text fontSize="12px" color="secondaryGray.600" mt="4px">{l}</Text></Box>
    </Flex></Card>
  );
}

/** Câu sai nhiều nhất — server xếp sẵn theo số lần sai, chỉ lấy 8 dòng. */
function TopWrong({ childId, qsStr }) {
  const soft = useColorModeValue('#FBFAFF', '#1E1840');
  const { data } = useApi(
    () => (childId
      ? api.get(`/reports/children/${childId}/answers?wrongOnly=true&sort=wrong&pageSize=8${qsStr ? `&${qsStr}` : ''}`)
      : Promise.resolve(null)),
    [childId, qsStr],
  );
  const rows = data?.items || [];
  if (!rows.length) return null;
  return (
    <Card gridColumn={{ lg: '1 / -1' }}>
      <Text fontWeight="800" fontSize="14.5px" mb="12px">❗ Câu sai nhiều nhất</Text>
      <Flex direction="column" gap="9px">
        {rows.map((a, i) => (
          <Flex key={i} align="center" gap="12px" p="9px 12px" borderRadius="12px" bg={soft}>
            <Badge colorScheme="red" fontSize="12px" borderRadius="8px" px="8px" flex="0 0 auto">{a.wrong}× sai</Badge>
            <Text flex="1" fontWeight="700" fontSize="13.5px" noOfLines={1}>{a.q}</Text>
            <Text fontSize="12px" color="secondaryGray.600" whiteSpace="nowrap" flex="0 0 auto">Lv {a.level ?? '—'} · {a.correct}/{a.attempts} đúng</Text>
          </Flex>
        ))}
      </Flex>
    </Card>
  );
}

function AnswersLog({ childId, qsStr }) {
  const [wrongOnly, setWrongOnly] = useState(false);
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const head = useColorModeValue('#FBFAFF', '#1E1840');
  // Một bé có thể có vài nghìn câu distinct -> lọc/tìm/phân trang đều làm ở server.
  const pager = useServerPaged(
    (p) => api.get(`/reports/children/${childId}/answers?page=${p.page}&pageSize=${p.pageSize}`
      + `${dq.trim() ? `&q=${encodeURIComponent(dq.trim())}` : ''}${wrongOnly ? '&wrongOnly=true' : ''}${qsStr ? `&${qsStr}` : ''}`),
    { initialSize: 20, resetKey: `${wrongOnly}|${dq.trim()}`, deps: [childId, qsStr, wrongOnly] },
  );
  return (
    <Card mt="16px" p="0" overflow="hidden">
      <Flex align="center" gap="10px" p="16px 18px" borderBottom="1px solid" borderColor="line" wrap="wrap">
        <Text fontWeight="850" fontSize="15.5px">📝 Nhật ký câu trả lời</Text>
        <Input size="sm" maxW="240px" placeholder="🔍 Tìm câu..." value={q} onChange={(e) => setQ(e.target.value)} borderRadius="10px" />
        <Flex ml="auto" gap="8px">
          <Button size="sm" variant={wrongOnly ? 'ghost' : 'solid'} colorScheme="brand" onClick={() => setWrongOnly(false)}>Tất cả</Button>
          <Button size="sm" variant={wrongOnly ? 'solid' : 'ghost'} colorScheme="brand" onClick={() => setWrongOnly(true)}>Chỉ câu sai</Button>
        </Flex>
      </Flex>
      <Box overflowX="auto">
        <Box as="table" w="100%" fontSize="13.5px" style={{ borderCollapse: 'collapse' }}>
          <Box as="thead"><Box as="tr">
            {['Câu hỏi', 'Lv', 'Số lần làm', 'Đúng/Sai', 'Bé chọn', 'Đáp án đúng', 'Trả lời lúc'].map((h) => (
              <Box as="th" key={h} textAlign="left" p="11px 16px" fontSize="11.5px" textTransform="uppercase"
                letterSpacing=".05em" color="secondaryGray.600" fontWeight="800" bg={head} whiteSpace="nowrap">{h}</Box>
            ))}
          </Box></Box>
          <Box as="tbody">
            {pager.pageItems.map((a, i) => {
              const pct = a.attempts ? Math.round(a.correct / a.attempts * 100) : 0;
              const wrong = a.wrong > 0;
              return (
                <Box as="tr" key={i}>
                  <Box as="td" p="12px 16px" borderTop="1px solid" borderColor="line" fontWeight="700" maxW="360px">{a.q}</Box>
                  <Box as="td" p="12px 16px" borderTop="1px solid" borderColor="line" color="secondaryGray.600">{a.level ?? '—'}</Box>
                  <Box as="td" p="12px 16px" borderTop="1px solid" borderColor="line" fontWeight="800">{a.attempts} lần</Box>
                  <Box as="td" p="12px 16px" borderTop="1px solid" borderColor="line">
                    <Flex align="center" gap="8px" minW="130px">
                      <Badge colorScheme="green">{a.correct} ✓</Badge>
                      <Badge colorScheme={wrong ? 'red' : 'gray'}>{a.wrong} ✗</Badge>
                      <Box flex="1" h="7px" borderRadius="99px" bg="rgba(248,113,113,.3)" overflow="hidden" minW="50px">
                        <Box h="100%" w={`${pct}%`} bg="#22C55E" /></Box>
                    </Flex>
                  </Box>
                  <Box as="td" p="12px 16px" borderTop="1px solid" borderColor="line"
                    color={a.chosen && a.ans && a.chosen.trim() === a.ans.trim() ? 'green.500' : 'red.400'}
                    fontWeight="700" textDecoration={a.chosen && a.ans && a.chosen.trim() === a.ans.trim() ? 'none' : 'line-through'}>{a.chosen || '—'}</Box>
                  <Box as="td" p="12px 16px" borderTop="1px solid" borderColor="line" color="green.500" fontWeight="700">{a.ans || '—'}</Box>
                  <Box as="td" p="12px 16px" borderTop="1px solid" borderColor="line" color="secondaryGray.600" whiteSpace="nowrap">
                    {a.lastTs ? new Date(a.lastTs).toLocaleString('vi-VN') : '—'}</Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      {pager.total > 0 && <Box p="8px 18px 14px"><Pagination pager={pager} label="câu" /></Box>}
      {!pager.total && (
        <Text p="16px 18px" color="secondaryGray.600">
          {pager.loading ? 'Đang tải…' : 'Chưa có dữ liệu câu trả lời.'}
        </Text>
      )}
    </Card>
  );
}

const LBL = {
  subject: Object.fromEntries(SUBJECTS), difficulty: Object.fromEntries(DIFFS),
  mode: Object.fromEntries(MODES), answerMode: Object.fromEntries(ANSWER),
};

function ConfigCard({ child, onSaved }) {
  const toast = useToast();
  const { isOpen: editing, onOpen: startEdit, onClose: stopEdit } = useDisclosure();
  const confirm = useDisclosure();
  const [f, setF] = useState(baseForm(child));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  function baseForm(c) {
    return {
      avatar: c.avatar || '', grade: c.grade || 'lop1', subject: c.subject || 'english', difficulty: c.difficulty || 'medium',
      mode: c.mode || 'quiz', answerMode: c.answerMode || 'choice',
      requiredStreak: c.requiredStreak ?? 20, sessionMin: c.sessionMin ?? 120,
      countdownSec: c.countdownSec ?? 30, listenChance: c.listenChance ?? 50, message: c.message || '',
    };
  }
  function beginEdit() { setF(baseForm(child)); startEdit(); }

  async function doSave() {
    setBusy(true);
    try {
      await api.patch(`/children/${child.id}`, {
        ...f, avatar: f.avatar || undefined, requiredStreak: Number(f.requiredStreak), sessionMin: Number(f.sessionMin),
        countdownSec: Number(f.countdownSec), listenChance: Number(f.listenChance),
      });
      toast({ title: 'Đã lưu cấu hình. Máy con sẽ cập nhật khi online.', status: 'success' });
      confirm.onClose(); stopEdit(); onSaved && onSaved();
    } catch (e) { toast({ title: e.message, status: 'error' }); } finally { setBusy(false); }
  }

  if (!editing) {
    // ----- VIEW -----
    return (
      <Card mb="16px">
        <Flex align="center" mb="14px"><Text fontWeight="850" fontSize="16px">⚙️ Cấu hình học tập</Text>
          <Button ml="auto" size="sm" variant="outline" leftIcon={<MdEdit />} onClick={beginEdit}>Sửa</Button></Flex>
        <SimpleGrid columns={{ base: 2, md: 3 }} gap="12px">
          <CfgItem l="Môn" v={LBL.subject[child.subject] || child.subject} />
          <CfgItem l="Lớp / Cấp" v={String(child.grade).toUpperCase()} />
          <CfgItem l="Độ khó" v={LBL.difficulty[child.difficulty] || child.difficulty} />
          {child.difficulty === 'superhard' && <CfgItem l="⏱️ Đếm ngược / câu" v={`${child.countdownSec ?? 30}s`} />}
          <CfgItem l="Kiểu chơi" v={LBL.mode[child.mode] || child.mode} />
          <CfgItem l="Cách trả lời" v={LBL.answerMode[child.answerMode] || child.answerMode} />
          <CfgItem l="Số câu mở khoá" v={child.requiredStreak} />
          <CfgItem l="Phút / phiên" v={`${child.sessionMin}′`} />
          <CfgItem l="🔊 Luyện nghe" v={`${child.listenChance ?? 50}%`} />
        </SimpleGrid>
        {child.message && (
          <Box mt="12px" p="12px 14px" borderRadius="14px" fontWeight="600"
            bgGradient="linear(135deg, rgba(124,92,252,.09), rgba(90,200,250,.09))" border="1px solid" borderColor="rgba(124,92,252,.25)">
            💌 “{child.message}”
          </Box>
        )}
      </Card>
    );
  }

  // ----- EDIT -----
  return (
    <Card mb="16px" border="1.5px solid" borderColor="brand.500">
      <Flex align="center" mb="14px"><Text fontWeight="850" fontSize="16px">✏️ Sửa cấu hình</Text>
        <Badge ml="10px" colorScheme="purple">chế độ sửa</Badge></Flex>
      <FormControl mb="14px"><FormLabel fontSize="sm" fontWeight="600">Avatar của con (chọn 1)</FormLabel>
        <Flex gap="8px" wrap="wrap">
          {AVATARS.map((a) => (
            <Flex key={a} w="44px" h="44px" borderRadius="12px" align="center" justify="center" fontSize="24px" cursor="pointer"
              bg="whiteAlpha.200" border="2px solid" borderColor={f.avatar === a ? 'brand.500' : 'transparent'}
              boxShadow={f.avatar === a ? '0 0 0 3px rgba(124,92,252,.18)' : 'none'}
              onClick={() => setF({ ...f, avatar: a })}>{a}</Flex>
          ))}
        </Flex>
      </FormControl>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap="14px">
        <FormControl><FormLabel fontSize="sm" fontWeight="600">Lớp / Cấp độ</FormLabel><Input value={f.grade} onChange={set('grade')} placeholder="vd: lop6, n2" /></FormControl>
        <FormControl><FormLabel fontSize="sm" fontWeight="600">Môn học</FormLabel><Select value={f.subject} onChange={set('subject')}>{SUBJECTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></FormControl>
        <FormControl><FormLabel fontSize="sm" fontWeight="600">Độ khó</FormLabel><Select value={f.difficulty} onChange={set('difficulty')}>{DIFFS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></FormControl>
        {f.difficulty === 'superhard' && (
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="600">⏱️ Đếm ngược mỗi câu (giây)</FormLabel>
            <Input type="number" value={f.countdownSec} onChange={set('countdownSec')} min={5} max={300} />
            <Text fontSize="12px" color="secondaryGray.600" mt="4px">Hết giờ mà chưa trả lời = tính là sai (trắc nghiệm) / thua lượt (kho báu).</Text>
          </FormControl>
        )}
        <FormControl><FormLabel fontSize="sm" fontWeight="600">Kiểu chơi</FormLabel><Select value={f.mode} onChange={set('mode')}>{MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></FormControl>
        <FormControl><FormLabel fontSize="sm" fontWeight="600">Cách trả lời</FormLabel><Select value={f.answerMode} onChange={set('answerMode')}>{ANSWER.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></FormControl>
        <FormControl><FormLabel fontSize="sm" fontWeight="600">Số câu đúng để mở khoá</FormLabel><Input type="number" value={f.requiredStreak} onChange={set('requiredStreak')} /></FormControl>
        <FormControl><FormLabel fontSize="sm" fontWeight="600">Số phút mỗi phiên</FormLabel><Input type="number" value={f.sessionMin} onChange={set('sessionMin')} /></FormControl>
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600">🔊 Luyện nghe: {f.listenChance}% câu từ vựng đọc lên</FormLabel>
          <Slider value={Number(f.listenChance)} min={0} max={100} step={10}
            onChange={(v) => setF((s) => ({ ...s, listenChance: v }))}>
            <SliderTrack><SliderFilledTrack /></SliderTrack>
            <SliderThumb boxSize={6} />
          </Slider>
          <Text fontSize="xs" color="gray.500" mt="1">Câu từ vựng tiếng Anh sẽ được đọc lên (con nghe rồi chọn nghĩa) theo tỉ lệ này. 0 = tắt.</Text>
        </FormControl>
      </SimpleGrid>
      <FormControl mt="14px" mb="16px"><FormLabel fontSize="sm" fontWeight="600">Lời nhắn cho con 💌</FormLabel>
        <Textarea value={f.message} onChange={set('message')} placeholder="Cố lên con nhé, ba mẹ yêu con!" rows={2} maxLength={200} /></FormControl>
      <Flex gap="8px"><Button variant="ghost" onClick={stopEdit}>Huỷ</Button><Button colorScheme="brand" onClick={confirm.onOpen}>💾 Lưu cấu hình</Button></Flex>

      <Modal isOpen={confirm.isOpen} onClose={confirm.onClose} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="18px">
          <ModalHeader>Xác nhận lưu?</ModalHeader>
          <ModalBody><Text color="secondaryGray.600">Cấu hình mới sẽ đồng bộ xuống máy của {child.name} ở lần online kế tiếp.</Text></ModalBody>
          <ModalFooter gap="8px"><Button variant="ghost" onClick={confirm.onClose}>Xem lại</Button>
            <Button colorScheme="green" onClick={doSave} isLoading={busy}>Đồng ý lưu</Button></ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}
function CfgItem({ l, v }) {
  const bg = useColorModeValue('#FBFAFF', '#1E1840');
  return (
    <Box bg={bg} border="1px solid" borderColor="line" borderRadius="13px" p="11px 13px">
      <Text fontSize="11px" color="secondaryGray.600" textTransform="uppercase" letterSpacing=".05em" fontWeight="700">{l}</Text>
      <Text fontWeight="800" mt="3px" fontSize="14.5px">{v ?? '—'}</Text>
    </Box>
  );
}
